
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Check,
  X,
  Edit,
  Star,
  TrendingUp,
  Trash2,
  MoreVertical,
  ArrowLeft,
  Home,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { toast } from '@/hooks/use-toast';
import { getAdminProperties, updatePropertyTrending, updatePropertyFeatured, deleteProperty as deletePropertyApi } from '@/services/property';
import { getAllUsers } from '@/services/user';
import PropertyCard from '@/components/ui/PropertyCard';
import { propertyTypes, localities, propertyStatuses, adminPropertyStatuses, developers as developersOptions, propertyFlags, pagination } from '@/constants';
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords } from '@/helper';

const PropertyManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [properties, setProperties] = useState<any[]>([]);
  const [brokers, setBrokers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const broker = searchParams.get('broker');

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [brokerFilter, setBrokerFilter] = useState<string>(broker || 'all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [developerFilter, setDeveloperFilter] = useState<string>('all');
  const [flagFilter, setFlagFilter] = useState<string>('all');

  // Fetch properties on component mount
  useEffect(() => {
    fetchProperties(1);
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      // Fetch all brokers for the dropdown
      const { data } = await getAllUsers({
        userType: 'BROKER',
      });

      if (Array.isArray(data)) {
        setBrokers(data);
      } else {
        console.error('Invalid data format received from getAllUsers: ', data);
      }
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchProperties = async (page = 1) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        limit: pagination.limit,
        searchQuery: searchQuery, // Search query
        propertyStatus: statusFilter !== 'all' ? statusFilter : undefined,
        approvalStatus: approvalStatusFilter !== 'all' ? approvalStatusFilter : undefined,
        propertyType: typeFilter !== 'all' ? typeFilter : undefined,
        broker: brokerFilter !== 'all' ? brokerFilter : undefined,
        location: locationFilter !== 'all' ? locationFilter : undefined,
        developer: developerFilter !== 'all' ? developerFilter : undefined,
        flag: flagFilter !== 'all' ? flagFilter : undefined,
      };

      const { data, pagination: paginationData } = await getAdminProperties(params);

      if (Array.isArray(data)) {
        setProperties(data);
        setTotalPages(paginationData?.totalPages || 1);
        setTotalProperties(paginationData?.totalProperties || data.length);
        setCurrentPage(page);
      } else {
        setProperties([]);
        setTotalPages(1);
        setTotalProperties(0);
        setCurrentPage(1);
      }
    } catch (error: any) {
      console.error('Error fetching properties:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load properties",
        variant: "destructive"
      });
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const statusColors = {
    DRAFT: 'bg-muted text-muted-foreground hover:bg-muted',
    PENDING: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    PUBLISHED: 'bg-green-100 text-green-800 hover:bg-green-100',
    REJECTED: 'bg-red-100 text-red-800 hover:bg-red-100'
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchProperties(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset to page 1 when filters change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProperties(1);
    }, 500); // Debounce search
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, approvalStatusFilter, typeFilter, brokerFilter, locationFilter, developerFilter, flagFilter]);

  // Toggle property selection for bulk actions
  const togglePropertySelection = (id: string) => {
    setSelectedProperties(prev =>
      prev.includes(id) ? prev.filter(propId => propId !== id) : [...prev, id]
    );
  };

  // Select all properties
  const selectAllProperties = () => {
    if (selectedProperties.length === properties.length) {
      setSelectedProperties([]);
    } else {
      setSelectedProperties(properties.map(p => p._id));
    }
  };

  // Toggle featured status
  const toggleFeatured = async (id: string, current: boolean) => {
    try {
      await updatePropertyFeatured(id, !current);
      toast({
        title: !current ? "Added to Featured" : "Removed from Featured",
        description: !current ? "Property added to featured section." : "Property removed from featured section.",
      });
      fetchProperties(currentPage); // Refresh current page
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update featured status",
        variant: "destructive"
      });
    }
  };

  // Toggle trending status
  const toggleTrending = async (id: string, current: boolean) => {
    try {
      await updatePropertyTrending(id, !current);
      toast({
        title: !current ? "Added to Trending" : "Removed from Trending",
        description: !current ? "Property added to trending section." : "Property removed from trending section.",
      });
      fetchProperties(currentPage);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update trending status",
        variant: "destructive"
      });
    }
  };

  // Toggle off plan status
  const toggleOffPlan = (id: string, current: boolean) => {
    toast({
      title: current ? "Removed from Off Plan" : "Added to Off Plan",
      description: current ? "Property removed from off plan section." : "Property added to off plan section.",
    });
  };

  // Delete property
  const deleteProperty = async () => {
    if (!selectedProperty) return;

    try {
      await deletePropertyApi(selectedProperty);
      toast({
        title: "Property Deleted",
        description: "The property has been permanently deleted.",
      });
      fetchProperties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete property",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedProperty(null);
    }
  };

  // Bulk update properties
  const bulkUpdateProperties = (action: string) => {
    if (selectedProperties.length === 0) {
      toast({
        title: "No Properties Selected",
        description: "Please select at least one property.",
        variant: "destructive",
      });
      return;
    }

    switch (action) {
      case 'publish':
        toast({
          title: "Properties Published",
          description: `${selectedProperties.length} properties have been published.`,
        });
        break;
      case 'unpublish':
        toast({
          title: "Properties Unpublished",
          description: `${selectedProperties.length} properties have been unpublished.`,
        });
        break;
      case 'feature':
        toast({
          title: "Properties Featured",
          description: `${selectedProperties.length} properties have been marked as featured.`,
        });
        break;
      case 'trending':
        toast({
          title: "Properties Trending",
          description: `${selectedProperties.length} properties have been marked as trending.`,
        });
        break;
      case 'delete':
        toast({
          title: "Properties Deleted",
          description: `${selectedProperties.length} properties have been deleted.`,
        });
        break;
      default:
        break;
    }

    setSelectedProperties([]);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setApprovalStatusFilter('all');
    setTypeFilter('all');
    setBrokerFilter('all');
    setLocationFilter('all');
    setFlagFilter('all');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="pt-32 pb-20 bg-secondary/30 flex-1">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Property Management</h1>
              <p className="text-muted-foreground">Manage all properties on the platform</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to="/admin/dashboard">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Link>
              </Button>
              <Button asChild>
                <Link to="/admin/property-upload">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Property
                </Link>
              </Button>
            </div>
          </div>

          {/* Filters Section */}
          <Card className="mb-8">
            <CardHeader className="pb-3">
              <CardTitle>Filters</CardTitle>
              <CardDescription>Refine property listing results</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search by title, location..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Availability</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Availabilities</SelectItem>
                      {propertyStatuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Approval Status</label>
                  <Select value={approvalStatusFilter} onValueChange={setApprovalStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {adminPropertyStatuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Property Type</label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {propertyTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Broker</label>
                  <Select value={brokerFilter} onValueChange={setBrokerFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by broker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Brokers</SelectItem>
                      {brokers.map(broker => (
                        <SelectItem key={broker._id} value={broker._id}>
                          {broker.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Location</label>
                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {localities.map(locality => (
                        <SelectItem key={locality.value} value={locality.value}>
                          {locality.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Developer</label>
                  <Select value={developerFilter} onValueChange={setDeveloperFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by developer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Developers</SelectItem>
                      {developersOptions.map(developer => (
                        <SelectItem key={developer.value} value={developer.value}>
                          {developer.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Flags</label>
                  <Select value={flagFilter} onValueChange={setFlagFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by flags" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Flags</SelectItem>
                      {propertyFlags.map(flag => (
                        <SelectItem key={flag.value} value={flag.value}>
                          {flag.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <div className="flex justify-between w-full">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
                <div className="text-sm text-muted-foreground">
                  Showing {properties.length} of {totalProperties} properties
                </div>
              </div>
            </CardFooter>
          </Card>

          {/* Bulk Actions */}
          {selectedProperties.length > 0 && (
            <div className="bg-primary/5 rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center">
                <span className="text-sm font-medium mr-2">
                  {selectedProperties.length} properties selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedProperties([])}
                >
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdateProperties('publish')}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Publish
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdateProperties('unpublish')}
                >
                  <X className="h-4 w-4 mr-2" />
                  Unpublish
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdateProperties('feature')}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Feature
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkUpdateProperties('trending')}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Trending
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => bulkUpdateProperties('delete')}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}

          {/* View Toggle */}
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
            >
              {viewMode === 'grid' ? 'Table View' : 'Grid View'}
            </Button>
          </div>

          {/* Properties Grid and Table Views with Loading State */}
          {isLoading ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardHeader className="pb-2">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardHeader>
                    <CardContent className="pb-4">
                      <div className="flex justify-between items-center">
                        <Skeleton className="h-6 w-24" />
                        <Skeleton className="h-6 w-20 rounded-full" />
                      </div>
                    </CardContent>
                    <CardFooter className="pt-0 flex gap-2">
                      <Skeleton className="h-9 flex-1" />
                      <Skeleton className="h-9 flex-1" />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Broker</TableHead>
                      <TableHead>Flags</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Skeleton className="h-10 w-10 rounded-md" />
                            <div className="space-y-1">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.length > 0 ? properties.map((property) => (
                <PropertyCard
                  key={property._id}
                  property={property}
                  type={property.Featured ? 'featured' : (property.Trending ? 'trending' : undefined)}
                  actions={
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        asChild
                      >
                        <Link to={`/admin/property-edit/${property._id}`}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Link>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1">
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Manage Property</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => toggleFeatured(property._id, !!property.Featured)}>
                            {property.Featured ? 'Remove from Featured' : 'Mark as Featured'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleTrending(property._id, !!property.Trending)}>
                            {property.Trending ? 'Remove from Trending' : 'Mark as Trending'}
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/properties/${property.slug}`}>View on Site</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setSelectedProperty(property._id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  }
                />
              )) : (
                <div className="col-span-full text-center py-10">
                  <p className="text-muted-foreground">No properties found matching your filters.</p>
                </div>
              )}
            </div>
          )}

          {/* Properties Table View */}
          {viewMode === 'table' && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Broker</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.length > 0 ? properties.map((property) => (
                    <TableRow key={property._id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-md overflow-hidden bg-muted">
                            {property.media?.images?.[0] ? (
                              <img
                                src={encodeS3Url(property.media.images[0])}
                                alt={capitalizeWords(property.specifications?.titleDeed || 'Property')}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                <Home className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium line-clamp-1">{capitalizeWords(property.specifications?.titleDeed || 'Untitled')}</div>
                            <div className="text-xs text-muted-foreground">{property.management?.reference || property._id.substring(0, 8)}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{capitalizeWords(property.location?.locality || 'N/A')}</TableCell>
                      <TableCell>{formatPrice(property.pricing?.amount || 0)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[property.status as keyof typeof statusColors]}>
                          {property.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{capitalizeWords(property.createdBy?.name || 'Unknown')}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {property.Featured && (
                            <Badge variant="outline" className="border-gold text-gold">F</Badge>
                          )}
                          {property.Trending && (
                            <Badge variant="outline" className="border-primary text-primary">T</Badge>
                          )}
                          {property.propertyCategory === 'OFF_PLAN' && (
                            <Badge variant="outline" className="border-blue-600 text-blue-600">OP</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/property-edit/${property._id}`}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleFeatured(property._id, !!property.Featured)}>
                              <Star className="h-4 w-4 mr-2" />
                              {property.Featured ? 'Remove from Featured' : 'Mark as Featured'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleTrending(property._id, !!property.Trending)}>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              {property.Trending ? 'Remove from Trending' : 'Mark as Trending'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedProperty(property._id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No properties found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-8 flex justify-center">
            <PaginationControl
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteProperty}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default PropertyManagement;
